import React, { useEffect, useRef, useState } from "react";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import { icon } from "../../config";
import { useWorkflowContext } from "../Workflow/WorkflowContextProvider";
import { VARIABLE_ICONS } from "../Workflow/WorkflowVariables/workflowVariables.constants";
import DragHandle from "./DragHandle/DragHandle";
import FormBuilderFooter from "./FormBuilderFooter";
import { AdvTextComponent, TableFormField, TextComponent } from "./FormFieldType";
import { FORM_ICONS_UPDATED } from "./FormsConstant";
import RowContainer from "./RowContainer/RowContainer";
import { StrictModeDroppable } from "./StrictModeDroppable/StrictModeDroppable";
import SubsectionHeader from "./SubsectionHeader/SubsectionHeader";
import TableContainer from "./TableContainer/TableContainer";
import { useFormInteraction } from "./contexts/FormInteractionContext";
import { useFormReorder } from "./hooks/useFormReorder";
import { useSubsectionExpand } from "./hooks/useSubsectionExpand";
import "./styles/FormCanvas.scss";

/**
 * Form Canvas Component
 * Main workspace where form fields are arranged and edited
 */
const FormCanvas = ({
  formData,
  onDrop,
  onElementSelect,
  onElementDelete,
  onSubsectionDelete,
  onAddSubsection,
  onSave,
  onCancel,
  onLabelChange,
  saveButtonText,
  onFieldPropertiesChange,
  isEditable,
  onSaveVariables,
  onElementReorder,
  setFormData,
  formTabs,
}) => {
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const { setIsNewWorkflowVariable, customWorkflowVariables } = useWorkflowContext();
  const { selectedElement, selectedSubsection, setSelectedElement, setSelectedSubsection } = useFormInteraction();
  // Initialize form reordering hook
  const { handleDragEnd, isDraggable } = useFormReorder({
    formData,
    setFormData,
    setSelectedElement,
    customWorkflowVariables,
    formTabs,
  });

  // Initialize subsection expand/collapse hook with localStorage persistence
  const { toggleExpanded, expandAll, collapseAll, expandSubsection, isExpanded, areAllExpanded } = useSubsectionExpand(
    formData?.[formTabs?.hostSection] || []
  );

  // Handle adding new subsection (should be expanded by default)
  const handleAddSubsection = () => {
    const newSubsectionId = onAddSubsection();
    // Expand the new subsection after a short delay
    if (newSubsectionId) {
      setTimeout(() => {
        expandSubsection(newSubsectionId);
      }, 100);
    }
    return newSubsectionId;
  };

  const parseComponentFromEvent = (event) => {
    event.preventDefault();
    setDragOverIndex(null);
    // Get the component data from drag
    const componentData = event.dataTransfer.getData("component");
    if (!componentData) return null;
    try {
      return JSON.parse(componentData);
    } catch (error) {
      console.error("Error parsing dropped component:", error);
      return null;
    }
  };

  const handleOuterDrop = (event) => {
    const component = parseComponentFromEvent(event);
    if (component && onDrop) {
      const newSubsectionId = onDrop(component, { subsectionId: null });
      // If a new subsection was created, expand it
      if (newSubsectionId) {
        setTimeout(() => {
          expandSubsection(newSubsectionId);
        }, 100);
      }
    }
  };

  const handleInnerDrop = (event, subsectionId) => {
    event.stopPropagation();
    const component = parseComponentFromEvent(event);
    if (component && onDrop) onDrop(component, { subsectionId });
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    // Add visual feedback for drag over
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOverIndex(null);
  };

  const handleFieldClick = (element) => {
    if (onElementSelect) {
      onElementSelect(element);
      setIsNewWorkflowVariable((prev) => false);
    }
    // Also update the context selection (this will deselect subsection)
    setSelectedElement(element);
    // setSelectedSubsection(null);
  };

  const handleSubsectionSelect = (subsection) => {
    setSelectedSubsection(subsection);
    // setSelectedElement(null);
  };

  const handleCanvasClick = (e) => {
    // Clear selections when clicking on empty canvas area
    if (e.target === e.currentTarget || e.target.classList.contains("canvas-container")) {
      setSelectedElement(null);
      setSelectedSubsection(null);
    }
  };

  const handleFieldDelete = (fieldId) => {
    if (onElementDelete) {
      onElementDelete(fieldId);
    }
  };

  const handleSubsectionDelete = (subsectionId) => {
    if (onSubsectionDelete) {
      onSubsectionDelete(subsectionId);
    }
  };

  // Handle adding a new row to existing row containers in a subsection
  const handleAddRow = (targetElement = null) => {
    if (targetElement) {
      // Update the row container to have one more row
      if (onFieldPropertiesChange) {
        onFieldPropertiesChange(targetElement.id, {
          rowCount: (targetElement.properties?.rowCount || 1) + 1,
        });
      }
    }
  };

  // Handle drop into row container grid cell
  const handleRowColumnDrop = (rowElementId, columnIndex, droppedComponent, gridPosition) => {
    if (onDrop) {
      const rowIndex = gridPosition?.rowIndex ?? 0;

      onDrop(droppedComponent, {
        rowElementId,
        columnIndex,
        rowIndex,
        isRowDrop: true,
      });
    }
  };

  // Handle delete element from grid cell
  const handleGridElementDelete = (elementId, rowIndex, columnIndex) => {
    if (onFieldPropertiesChange) {
      // We need to find which grid container this element belongs to and clear that cell
      // This will be handled by FormBuilder's property change handler
      onFieldPropertiesChange("GRID_CELL_DELETE", {
        elementId,
        rowIndex,
        columnIndex,
      });
    }
  };

  const hasSubsections = formData?.[formTabs?.hostSection] && formData?.[formTabs?.hostSection]?.length > 0;

  return (
    <div className='d-flex flex-column'>
      <div
        className='form-canvas d-flex flex-column overflow-y-auto'
        style={{ backgroundColor: formData?.[formTabs?.bgColorKey] || formTabs?.bgColor }}>
        <DragDropContext onDragEnd={onElementReorder || handleDragEnd}>
          <div
            className={`canvas-workspace ${dragOverIndex !== null ? "drag-over" : ""}`}
            onDrop={handleOuterDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onClick={handleCanvasClick}>
            {hasSubsections ? (
              <div className='subsections'>
                {formData?.[formTabs.hostSection]?.map((sub, sIdx) => {
                  const isSubsectionExpanded = isExpanded(sub.id);
                  return (
                    <div
                      key={sub.id || sIdx}
                      className={`subsection ${selectedSubsection?.id === sub.id ? "selected-subsection" : ""}`}
                      style={{ backgroundColor: sub.color || "#ffffff" }}>
                      <SubsectionHeader
                        subsection={sub}
                        subsectionIndex={sIdx + 1}
                        isExpanded={isSubsectionExpanded}
                        isSelected={selectedSubsection?.id === sub.id}
                        onSelect={handleSubsectionSelect}
                        onToggleExpanded={toggleExpanded}
                        onDelete={handleSubsectionDelete}
                      />
                      <StrictModeDroppable droppableId={sub.id} key={sub.id}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            onDrop={(e) => handleInnerDrop(e, sub.id)}
                            className={`subsection-dropzone position-relative ${snapshot.isDraggingOver ? "drag-over" : ""} ${
                              !isSubsectionExpanded ? "collapsed-subsection" : ""
                            }`}>
                            {isSubsectionExpanded &&
                              sub.elements
                                .filter((element) => {
                                  if (element.properties?.isWorkflowVariable) {
                                    const usedVar = customWorkflowVariables?.find((v) => v.id === element.properties.workflowVariableId);
                                    return usedVar ? true : false;
                                  }
                                  return true;
                                })
                                .filter((element) => !element.properties?.isHidden) // Skip hidden workflow variable elements
                                .map((element, index) => {
                                  const isSelected = selectedElement?.id === element.id;
                                  const isTextLabel = element.type === "text-label";
                                  const isAdvancedText = element.type === "advanced-text";
                                  const isRowContainer = element.type === "row-container";
                                  const isTable = element.type === "table";
                                  const isUserChip = element?.properties?.isUserVariable || element?.properties?.chipType === "user chip";

                                  // Handle row container separately
                                  if (isRowContainer) {
                                    return (
                                      <Draggable key={element.id} draggableId={element.id} index={index} isDragDisabled={!isDraggable(element)}>
                                        {(provided, snapshot) => (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            className={`form-element row-element ${snapshot.isDragging ? "dragging" : ""} d-flex align-items-center`}>
                                            <div className='flex-grow-1 mr-2'>
                                              <RowContainer
                                                element={element}
                                                onElementUpdate={onFieldPropertiesChange}
                                                selectedElement={selectedElement}
                                                onElementSelect={handleFieldClick}
                                                onDrop={handleRowColumnDrop}
                                                onElementDelete={handleGridElementDelete}
                                                onRowDelete={handleFieldDelete}
                                                onAddRow={handleAddRow}
                                              />
                                            </div>
                                            <DragHandle
                                              dragHandleProps={provided.dragHandleProps}
                                              isDragging={snapshot.isDragging}
                                              isVisible={isDraggable(element)}
                                            />
                                            <span
                                              className='red element-delete-icon row-delete-icon'
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleFieldDelete(element.id);
                                              }}>
                                              {icon.CLOSE}
                                            </span>
                                          </div>
                                        )}
                                      </Draggable>
                                    );
                                  }

                                  // Handle table separately
                                  if (isTable) {
                                    return (
                                      <Draggable key={element.id} draggableId={element.id} index={index} isDragDisabled={!isDraggable(element)}>
                                        {(provided, snapshot) => (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            className={`form-element table-element ${
                                              snapshot.isDragging ? "dragging" : ""
                                            } d-flex align-items-center`}>
                                            <div className='flex-grow-1 mr-2'>
                                              <TableContainer
                                                element={element}
                                                onElementUpdate={onFieldPropertiesChange}
                                                selectedElement={selectedElement}
                                                onElementSelect={handleFieldClick}
                                                onTableDelete={handleFieldDelete}
                                              />
                                            </div>
                                            <DragHandle
                                              dragHandleProps={provided.dragHandleProps}
                                              isDragging={snapshot.isDragging}
                                              isVisible={isDraggable(element)}
                                            />
                                            <span
                                              className='red element-delete-icon table-delete-icon'
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleFieldDelete(element.id);
                                              }}>
                                              {icon.CLOSE}
                                            </span>
                                          </div>
                                        )}
                                      </Draggable>
                                    );
                                  }

                                  // Handle regular elements
                                  return (
                                    <Draggable key={element.id} draggableId={element.id} index={index} isDragDisabled={!isDraggable(element)}>
                                      {(provided, snapshot) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          className={`form-element ${snapshot.isDragging ? "dragging" : ""}`}
                                          onClick={() => handleFieldClick(element)}>
                                          <div className='element-preview d-flex element-header align-items-center'>
                                            <div
                                              className={`preview-placeholder flex-grow-1 mr-2 ${
                                                isSelected && !isTextLabel && !isAdvancedText ? "selected" : ""
                                              }`}>
                                              {isTextLabel ? (
                                                <TextComponent
                                                  element={element}
                                                  formData={formData}
                                                  onFieldPropertiesChange={onFieldPropertiesChange}
                                                  isSelected={isSelected}
                                                  onLabelChange={onLabelChange}
                                                />
                                              ) : (
                                                <>
                                                  {isAdvancedText ? (
                                                    <></>
                                                  ) : element.properties?.isFormDimensionDisplay ? (
                                                    <div className='d-flex align-items-center'>
                                                      <span className='element-type-icon mr-3'>
                                                        {VARIABLE_ICONS[element.type] || VARIABLE_ICONS.text}
                                                      </span>
                                                      <span className='form-dimension-display-label'>{element.name}</span>
                                                    </div>
                                                  ) : isUserChip ? (
                                                    <div className='d-flex align-items-center'>
                                                      <span className='element-type-icon mr-3'>{icon.CONTACT_ROUND}</span>
                                                      {element.name}
                                                    </div>
                                                  ) : (
                                                    <div className='d-flex align-items-center'>
                                                      <span className='element-type-icon mr-3 d-flex align-items-center'>
                                                        {element.properties?.chipType === "smart chip"
                                                          ? VARIABLE_ICONS[element.type]
                                                          : FORM_ICONS_UPDATED[element.type]}
                                                      </span>
                                                      {element.name}
                                                    </div>
                                                  )}
                                                </>
                                              )}
                                              {element.type === "advanced-text" && (
                                                <AdvTextComponent
                                                  element={element}
                                                  formData={formData}
                                                  onFieldPropertiesChange={onFieldPropertiesChange}
                                                  isSelected={isSelected}
                                                  onLabelChange={onLabelChange}
                                                />
                                              )}
                                              {element.type === "table" && (
                                                <TableFormField
                                                  element={element}
                                                  formData={formData}
                                                  onFieldPropertiesChange={onFieldPropertiesChange}
                                                  isSelected={isSelected}
                                                />
                                              )}
                                            </div>
                                            <DragHandle
                                              dragHandleProps={provided.dragHandleProps}
                                              isDragging={snapshot.isDragging}
                                              isVisible={isDraggable(element)}
                                            />
                                            <span
                                              className='red element-delete-icon'
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleFieldDelete(element.id);
                                              }}>
                                              {icon.CLOSE}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </Draggable>
                                  );
                                })}
                            {provided.placeholder}
                          </div>
                        )}
                      </StrictModeDroppable>
                    </div>
                  );
                })}
                <div className='add-subsection-hint'>Drop in the outer area to create a new subsection</div>
              </div>
            ) : (
              <div className='empty-canvas'>
                <div className='empty-canvas-icon'>📃</div>
                <span className='empty-canvas-text'>Start building your form</span>
                <div className='empty-canvas-hint'>Drag the desired Components here</div>
                {/* Add Row Button for Empty Canvas */}
              </div>
            )}
          </div>
        </DragDropContext>
      </div>
      <FormBuilderFooter
        onAddSubsection={handleAddSubsection}
        onSave={onSave}
        onCancel={onCancel}
        saveButtonText={saveButtonText}
        isEditable={isEditable}
        onSaveVariables={onSaveVariables}
        onExpandAll={expandAll}
        onCollapseAll={collapseAll}
        areAllExpanded={areAllExpanded}
        hasSubsections={formData.subsections && formData.subsections.length > 0}
        formTabs={formTabs}
      />
    </div>
  );
};

export default FormCanvas;
